
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

interface AppSubscriptionButtonProps {
  priceId: string;
  successRedirectUrl?: string;
  cancelRedirectUrl?: string;
  children: React.ReactNode;
  className?: string;
}

const AppSubscriptionButton: React.FC<AppSubscriptionButtonProps> = ({
  priceId,
  successRedirectUrl,
  cancelRedirectUrl,
  children,
  className
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubscribe = async () => {
    setLoading(true);
    setErrorMessage(null);
    
    try {
      if (!user) {
        throw new Error("You must be logged in to subscribe");
      }
      
      console.log("Creating subscription checkout for", {
        priceId,
        userEmail: user.email
      });
      
      // Create checkout options that match the CheckoutSessionOptions type
      const checkoutOptions = {
        // Required fields from CheckoutSessionOptions
        amount_cents: 0, // Not used for subscriptions but required by the type
        organization_id: user.user_metadata?.organization_id || '',
        family_id: '', // Not used for subscriptions but required by the type
        description: 'Subscription', 
        success_url: `${window.location.origin}${successRedirectUrl || '/dashboard'}?subscription=success`,
        cancel_url: `${window.location.origin}${cancelRedirectUrl || '/dashboard'}?subscription=canceled`,
        
        // Additional fields specific to subscription checkout
        priceId,
        customer_email: user.email || '',
        metadata: {
          user_id: user.id,
        },
        trialDays: 14,
        allowPromoCodes: true
      };
      
      // Call the checkout function
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: checkoutOptions
      });
      
      if (error) {
        console.error("Error creating checkout session:", error);
        setErrorMessage(error.message);
        toast.error("Subscription failed", {
          description: error.message
        });
        return;
      }
      
      if (data?.url) {
        window.location.href = data.url;
      } else {
        console.error("No URL returned from checkout session creation");
        setErrorMessage("No URL returned from checkout session creation");
        toast.error("Subscription failed", {
          description: "No URL returned from checkout session creation"
        });
      }
    } catch (error: any) {
      console.error("Subscription Checkout Error:", error);
      setErrorMessage(error.message || "An unexpected error occurred");
      toast.error("Subscription failed", {
        description: error.message || "An unexpected error occurred"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button 
      className={className}
      onClick={handleSubscribe}
      disabled={loading}
    >
      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {children}
    </Button>
  );
};

export default AppSubscriptionButton;
